/**
 * The one delayed event queue. Both channels emit onto it.
 *
 * This is the module that makes the project's central claim work — that ASR
 * words and audio gestures compose on one timeline instead of fighting over the
 * same caption line. It does that with two corrections applied in order:
 *
 *   1. **Per-event latency.** A word reported by ASR now was spoken ~300 ms ago.
 *      A trill confirmed now was made ~340 ms ago, half its analysis window. A
 *      plosive burst is ~15 ms old. Each event carries its own `detectLatency`
 *      and gets it subtracted, which puts every event back where it belongs in
 *      *spoken* time.
 *
 *   2. **A global LAG.** Everything is then held back by the same display delay,
 *      long enough that the slowest detector has already reported by the time
 *      its moment is rendered.
 *
 * Skip step 1 and a "pow" lands before the word that preceded it, because the
 * fast detector wins a race it should never have been in. That is Phase 7's
 * acceptance test — "and then — pow — it stopped" — and it is decided here.
 *
 * On the spec's `GestureEvent.t` being "already latency-corrected": events go in
 * carrying their raw detection time and come out carrying their corrected
 * onset. `push()` is the moment of correction, so from the renderer's side —
 * which only ever sees what `due()` hands it — `t` is indeed always corrected.
 * Call `push()` once per event, with the time you noticed it.
 */

import { config } from './config.js';

/**
 * @template {{ t: number, detectLatency?: number }} T
 */
export class Timeline {
  /**
   * @param {object} [options]
   * @param {() => number} [options.lag] Display delay in ms, read fresh on every
   *   release rather than captured, so a Phase 8 slider takes effect on events
   *   already queued.
   */
  constructor({ lag } = {}) {
    this._lag = lag ?? (() => config.LAG);
    /** Ascending by corrected onset. @type {T[]} */
    this._queue = [];
  }

  /** Events waiting to be released. @returns {number} */
  get pending() {
    return this._queue.length;
  }

  /** Current display delay in ms. @returns {number} */
  get lag() {
    return this._lag();
  }

  /**
   * Queue an event, correcting its timestamp from detection time to onset time.
   *
   * Insertion keeps the queue sorted, because arrival order is not onset order:
   * a plosive detected 15 ms after the fact routinely arrives *after* an ASR
   * word that was spoken before it. Events with an identical onset keep their
   * insertion order.
   *
   * @param {T} event with `t` = time of detection, `detectLatency` = ms behind reality
   * @returns {T} the stored event, with `t` now the corrected onset
   */
  push(event) {
    const onset = event.t - (event.detectLatency ?? 0);
    const corrected = /** @type {T} */ ({ ...event, t: onset });

    // Scan from the newest end. Late-arriving stragglers are the exception, so
    // this lands in a step or two for essentially every event.
    let i = this._queue.length;
    while (i > 0 && this._queue[i - 1].t > onset) i--;
    this._queue.splice(i, 0, corrected);

    return corrected;
  }

  /**
   * Everything whose moment has arrived, oldest first, removed from the queue.
   *
   * @param {number} now performance.now()
   * @returns {T[]} possibly empty; never null
   */
  due(now) {
    const cutoff = now - this._lag();
    let n = 0;
    while (n < this._queue.length && this._queue[n].t <= cutoff) n++;
    return n ? this._queue.splice(0, n) : [];
  }

  /**
   * Next event awaiting release, without removing it.
   * @returns {T|null}
   */
  peek() {
    return this._queue.length ? this._queue[0] : null;
  }

  /** Oldest-first copy of the pending queue. Debugging and tests. @returns {T[]} */
  toArray() {
    return this._queue.slice();
  }

  clear() {
    this._queue.length = 0;
  }
}

export default Timeline;
